import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
	editPatient,
	fetchPatientDetail,
	registerPatient,
	resendInstallSms,
	revertPatient,
	closePatient,
	type PatientEditInput,
	type PatientListItem,
	type PatientRegisterInput,
} from '@/api/adapters/patients';
import { toast } from '@/app/store/toast.store';
import { ApiError } from '@/api/error';

const PATIENT_DETAIL_QUERY_KEY = ['patient', 'detail'] as const;

/**
 * Hook: fetch a single patient's detail by uid.
 * The query is disabled when uid is undefined or empty.
 *
 * `unmask` (default false) requests the server's unmasked record. Two callers pass true: the edit modal,
 * which pre-fills the registration form and needs real (editable) name/phone values (PRD 03 §3.3), and
 * the patient-detail header, which prints the patient's own 전화번호 in the clear so it can be dialled from
 * the same screen the phone intervention sits on (PRD 04 §3.1, added in v2.30 — SHCT-893). The masked and
 * unmasked reads are cached under different query keys, so the header can take the phone from the unmasked
 * entry while everything else on the page keeps reading the masked one.
 */
export function usePatientDetail(uid: string | undefined, unmask = false) {
	return useQuery<PatientListItem>({
		queryKey: [...PATIENT_DETAIL_QUERY_KEY, uid, unmask],
		queryFn: () => fetchPatientDetail(uid!, unmask),
		enabled: !!uid,
		staleTime: 60_000,
	});
}

/**
 * Mutation hook: register a new patient.
 * FEATURE_014 — 환자 등록
 */
export function useRegisterPatient() {
	const queryClient = useQueryClient();
	const { t } = useTranslation('medical');

	return useMutation({
		mutationFn: (input: PatientRegisterInput) => registerPatient(input),
		onSuccess: (_data, input) => {
			toast.success(t('patients.toast.registerSuccess', { name: input.name }));
			queryClient.invalidateQueries({ queryKey: ['patients'] }); // list + counts (SHCT-198 BE-1)
		},
		onError: (err) => {
			if (err instanceof ApiError) {
				// SHCT-500: 환자 휴대폰 번호 중복(병원 단위 유일) 시 BE가 PATIENT_DUPLICATE_PHONE 반환.
				// 문구는 의료진 회원가입 중복(Clarification No 63)과 동일 — 열거(enumeration) 공격 방지를 위해
				// 기존 환자를 노출하지 않는 두루뭉술한 안내. (MRN 중복과 달리 meta로 기존 환자를 붙이지 않음)
				if (err.code === 'PATIENT_DUPLICATE_PHONE') {
					toast.error(t('patients.toast.registerDuplicatePhone'));
				}
				// PRD §3.2/§8: MRN 중복 시 기존 환자 정보를 붙여 안내. BE는 base 메시지 + meta{uid,name,status}만
				// 내려주고(FE가 조합) — meta가 있으면 "({PID} 마스킹이름 · 상태)"까지 렌더, 없으면 base 메시지.
				else if (err.code === 'PATIENT_DUPLICATE_MRN' && err.meta) {
					const statusCode = String(err.meta.status ?? '');
					toast.error(
						t('patients.toast.registerDuplicate', {
							uid: err.meta.uid ?? '',
							name: err.meta.name ?? '',
							status: t(`patients.toast.statusLabel.${statusCode}`, {
								defaultValue: statusCode,
							}),
						}),
					);
				} else {
					toast.error(err.message);
				}
			} else {
				toast.error(t('patients.toast.registerFail'));
			}
		},
	});
}

/**
 * Mutation hook: edit a patient's information.
 * FEATURE_015 — 환자 정보 수정
 */
export function useEditPatient() {
	const queryClient = useQueryClient();
	const { t } = useTranslation('medical');

	return useMutation({
		mutationFn: ({ uid, data }: { uid: string; data: PatientEditInput }) =>
			editPatient(uid, data),
		onSuccess: (_result, { uid }) => {
			toast.success(t('patients.toast.editSuccess'));
			queryClient.invalidateQueries({ queryKey: [...PATIENT_DETAIL_QUERY_KEY, uid] });
			queryClient.invalidateQueries({ queryKey: ['patients'] }); // list + counts (SHCT-198 BE-1)
		},
		onError: (err) => {
			if (err instanceof ApiError) {
				// SHCT-500: editing a patient's phone to a number already used at the hospital also
				// collides with the app login identity — surface the same BE dup error (edit-context copy).
				if (err.code === 'PATIENT_DUPLICATE_PHONE') {
					toast.error(t('patients.toast.editDuplicatePhone'));
				} else {
					toast.error(err.message);
				}
			} else {
				toast.error(t('patients.toast.editFail'));
			}
		},
	});
}

/**
 * Mutation hook: revert a patient's unsaved edits back to the last committed state.
 * FEATURE_015 — 환자 정보 되돌리기
 */
export function useRevertPatient() {
	const queryClient = useQueryClient();
	const { t } = useTranslation('medical');

	return useMutation({
		mutationFn: (uid: string) => revertPatient(uid),
		onSuccess: (_data, uid) => {
			toast.success(t('patients.toast.revertSuccess'));
			queryClient.invalidateQueries({ queryKey: [...PATIENT_DETAIL_QUERY_KEY, uid] });
			queryClient.invalidateQueries({ queryKey: ['patients'] }); // list + counts (SHCT-198 BE-1)
		},
		onError: (err) => {
			if (err instanceof ApiError) {
				toast.error(err.message);
			} else {
				toast.error(t('patients.toast.revertFail'));
			}
		},
	});
}

/**
 * Mutation hook: close (end management for) a patient.
 * FEATURE_016 — 환자 관리 종료
 */
export function useClosePatient() {
	const queryClient = useQueryClient();
	const { t } = useTranslation('medical');

	return useMutation({
		mutationFn: ({
			uid,
			reason,
			endDate,
			memo,
		}: {
			uid: string;
			reason: string;
			endDate?: string;
			memo?: string;
		}) => closePatient(uid, reason, endDate, memo),
		onSuccess: (_data, { uid }) => {
			toast.success(t('patients.toast.closeSuccess'));
			queryClient.invalidateQueries({ queryKey: [...PATIENT_DETAIL_QUERY_KEY, uid] });
			queryClient.invalidateQueries({ queryKey: ['patients'] }); // list + counts (SHCT-198 BE-1)
		},
		onError: (err) => {
			if (err instanceof ApiError) {
				toast.error(err.message);
			} else {
				toast.error(t('patients.toast.closeFail'));
			}
		},
	});
}

/**
 * Mutation hook: send the app-installation guide again after a failed delivery.
 * FEATURE_014 — 환자 등록
 *
 * The server answers `success: false` — not an error — when the gateway refuses the resend too, so
 * the caller decides what to do next (keep the modal open to try again) rather than this hook
 * assuming a failed resend is fatal.
 */
export function useResendInstallSms() {
	const { t } = useTranslation('medical');

	return useMutation({
		mutationFn: (uid: string) => resendInstallSms(uid),
		onSuccess: ({ success }) => {
			if (success) {
				toast.success(t('patients.installSmsFailModal.resendSuccess'));
			}
		},
		onError: (err) => {
			if (err instanceof ApiError) {
				toast.error(err.message);
			}
		},
	});
}
